import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from '../../environments';

interface CognitoTokenResponse {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
}

type AuthFlow = 'dealer' | 'nsc';

interface PendingAuthorization {
  codeVerifier: string;
  state: string;
  returnUrl: string;
  authFlow?: AuthFlow;
  incomingContext?: IncomingAppContext;
}

export interface IncomingAppContext {
  language: 'en' | 'fr' | null;
  username: string | null;
  dealerCode: string | null;
}

interface CognitoConfiguration {
  domain: string;
  clientId: string;
  redirectUri: string;
  logoutUri: string;
  scopes: string[];
}

/**
 * Manages REM's Cognito Authorization Code flow with PKCE.
 */
@Injectable({ providedIn: 'root' })
export class AuthTokenService {
  private activeAuthFlow: AuthFlow = 'dealer';
  private readonly accessTokenSubject = new BehaviorSubject<string | null>(null);
  private accessTokenExpiresAt = 0;
  private refreshToken: string | null = null;
  // Set on logout; the page is leaving, so no request may restart the Cognito login.
  private isLoggingOut = false;
  // Cognito SSO completes silently, so repeated failures would otherwise redirect forever.
  private readonly loginAttemptsKey = 'rem_login_attempts';
  private readonly maxLoginAttempts = 2;
  private readonly loginAttemptsWindowMs = 30_000;

  private get configuration(): CognitoConfiguration {
    return this.activeAuthFlow === 'nsc' ? environment.cognito_NSC : environment.cognito;
  }

  readonly accessToken$ = this.accessTokenSubject.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router
  ) {
    this.logIncomingAppLink();
  }

  /**
   * Starts the Cognito Hosted UI authorization request and stores the PKCE transaction.
   */
  async startLogin(returnUrl = '/'): Promise<void> {
    if (this.isLoggingOut) {
      return;
    }

    if (!this.registerLoginAttempt()) {
      console.error('[REM flow] startLogin: too many sign-in attempts, stopping redirect loop');
      this.clearLocalTokens();
      void this.router.navigate(['/auth/error'], { queryParams: { reason: 'too-many-attempts' } });
      return;
    }

    console.log('[REM flow] startLogin returnUrl=', { returnUrl });
    const safeReturnUrl = this.toSafeReturnUrl(returnUrl);
    this.clearLocalTokens();
    this.activeAuthFlow = this.getAuthFlowForReturnUrl(safeReturnUrl);
    const configuration = this.configuration;
    this.assertConfiguration(configuration);

    const codeVerifier = this.createRandomValue(64);
    const state = this.createRandomValue(32);
    const codeChallenge = await this.createCodeChallenge(codeVerifier);
    const incomingContext = this.getIncomingAppContext(returnUrl);
    console.log('[REM flow] startLogin: incoming context', {
      language: incomingContext.language,
      dealerCode: incomingContext.dealerCode
    });
    const pendingAuthorization: PendingAuthorization = {
      codeVerifier,
      state,
      returnUrl: safeReturnUrl,
      authFlow: this.activeAuthFlow,
      incomingContext
    };

    sessionStorage.setItem('cognito_pkce_transaction', JSON.stringify(pendingAuthorization));

    const authorizationUrl = new URL(`https://${configuration.domain}/oauth2/authorize`);
    authorizationUrl.search = new URLSearchParams({
      response_type: 'code',
      client_id: configuration.clientId,
      redirect_uri: configuration.redirectUri,
      scope: configuration.scopes.join(' '),
      state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256'
    }).toString();

    window.location.assign(authorizationUrl.toString());
  }

  /**
   * Exchanges the one-time authorization code returned by Cognito for tokens.
   */
  handleCallback(callbackUrl = window.location.href): Observable<CognitoTokenResponse> {
    console.log('[REM flow] handleCallback: processing Cognito response');

    const callback = new URL(callbackUrl);
    const error = callback.searchParams.get('error');
    const code = callback.searchParams.get('code');
    const returnedState = callback.searchParams.get('state');
    const transactionJson = sessionStorage.getItem('cognito_pkce_transaction');

    if (error) {
      throw new Error(`Cognito authorization failed: ${error}`);
    }
    if (!code || !returnedState || !transactionJson) {
      throw new Error('The Cognito callback is missing authorization data.');
    }

    const transaction = JSON.parse(transactionJson) as PendingAuthorization;
    if (transaction.state !== returnedState) {
      throw new Error('The Cognito authorization state is invalid.');
    }

    this.activeAuthFlow = transaction.authFlow ?? this.getAuthFlowForReturnUrl(transaction.returnUrl);
    const configuration = this.configuration;
    this.assertConfiguration(configuration);

    const body = new HttpParams()
      .set('grant_type', 'authorization_code')
      .set('client_id', configuration.clientId)
      .set('code', code)
      .set('redirect_uri', configuration.redirectUri)
      .set('code_verifier', transaction.codeVerifier);

    return this.http
      .post<CognitoTokenResponse>(`https://${configuration.domain}/oauth2/token`, body, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      })
      .pipe(
        tap((tokens) => {
          this.setTokens(tokens);
          console.log('[REM flow] handleCallback: Cognito token exchange completed');
          sessionStorage.removeItem('cognito_pkce_transaction');
          window.history.replaceState({}, document.title, callback.pathname);
        })
      );
  }

  /**
   * Returns the internal route saved before the Cognito redirect.
   */
  getPendingReturnUrl(): string {
    const transactionJson = sessionStorage.getItem('cognito_pkce_transaction');
    if (!transactionJson) {
      return '/';
    }

    try {
      const transaction = JSON.parse(transactionJson) as PendingAuthorization;
      console.log('[REM flow] getPendingReturnUrl', { returnUrl: transaction.returnUrl });
      return this.toSafeReturnUrl(transaction.returnUrl);
    } catch {
      return '/';
    }
  }

  /** Selects the REM auth client from the protected route being requested. */
  getAuthFlowForReturnUrl(returnUrl: string): AuthFlow {
    const path = new URL(returnUrl, window.location.origin).pathname;
    return path === '/nsc' || path.startsWith('/nsc/') ? 'nsc' : 'dealer';
  }

  /** Returns true only when the in-memory token was obtained for the requested workflow. */
  isAuthenticatedFor(returnUrl: string): boolean {
    return this.isAuthenticated() && this.activeAuthFlow === this.getAuthFlowForReturnUrl(returnUrl);
  }

  /**
   * Returns the dealer context supplied by the first application before Cognito redirects the browser.
   */
  getPendingIncomingContext(): IncomingAppContext | null {
    const transactionJson = sessionStorage.getItem('cognito_pkce_transaction');
    if (!transactionJson) {
      return null;
    }

    try {
      return (JSON.parse(transactionJson) as PendingAuthorization).incomingContext ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Returns the current access token, or null when it is absent or expired.
   */
  getAccessToken(): string | null {
    const token = this.accessTokenSubject.value;
    return token && Date.now() < this.accessTokenExpiresAt ? token : null;
  }

  /**
   * Refreshes the access token using the in-memory Cognito refresh token.
   */
  refreshAccessToken(): Observable<CognitoTokenResponse> {
    if (!this.refreshToken) {
      throw new Error('No Cognito refresh token is available.');
    }

    const configuration = this.configuration;
    const body = new HttpParams()
      .set('grant_type', 'refresh_token')
      .set('client_id', configuration.clientId)
      .set('refresh_token', this.refreshToken);

    return this.http
      .post<CognitoTokenResponse>(`https://${configuration.domain}/oauth2/token`, body, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      })
      .pipe(tap((tokens) => this.setTokens(tokens)));
  }

  /**
   * Clears the local REM token state and sends the user back to the launching app for re-authentication.
   */
  handleAuthenticationExpired(reason: string): void {
    if (this.isLoggingOut) {
      return;
    }

    this.clearLocalTokens();
    const returnUrl = `${window.location.pathname}${window.location.search}`;
    void this.startLogin(returnUrl).catch(() => {
      void this.router.navigate(['/auth/error'], { queryParams: { reason } });
    });
  }

  /** Records a sign-in redirect; returns false when the limit within the time window is reached. */
  private registerLoginAttempt(): boolean {
    const now = Date.now();
    let attempts: { count: number; firstAt: number } = { count: 0, firstAt: now };
    try {
      const stored = JSON.parse(sessionStorage.getItem(this.loginAttemptsKey) ?? 'null');
      if (stored && now - stored.firstAt < this.loginAttemptsWindowMs) {
        attempts = stored;
      }
    } catch {
      // Corrupt value: start a new window.
    }

    if (attempts.count >= this.maxLoginAttempts) {
      return false;
    }

    sessionStorage.setItem(this.loginAttemptsKey, JSON.stringify({ ...attempts, count: attempts.count + 1 }));
    return true;
  }

  /** Clears the sign-in attempt counter once REM has fully loaded the user. */
  resetLoginAttempts(): void {
    sessionStorage.removeItem(this.loginAttemptsKey);
  }

  /**
   * Clears the in-memory Cognito session and starts Cognito logout when configured.
   */
  logout(flow: AuthFlow = this.activeAuthFlow): void {
    this.activeAuthFlow = flow;
    const configuration = this.configuration;
    this.clearLocalTokens();

    if (configuration.domain && configuration.clientId && configuration.logoutUri) {
      const logoutUrl = new URL(`https://${configuration.domain}/logout`);
      logoutUrl.search = new URLSearchParams({
        client_id: configuration.clientId,
        logout_uri: configuration.logoutUri
      }).toString();
      window.location.assign(logoutUrl.toString());
    }
  }

  /**
   * Returns whether an unexpired Cognito access token is available in memory.
   */
  isAuthenticated(): boolean {
    return this.getAccessToken() !== null;
  }

  /** Ends REM's Cognito session locally; the portal's own Cognito session is left to the portal. */
  clearSession(): void {
    this.isLoggingOut = true;
    this.clearLocalTokens();
  }

  /** Prevents any request failure from restarting the Cognito login while logout is in progress. */
  beginLogout(): void {
    this.isLoggingOut = true;
  }

  /**
   * Clears tokens owned by REM without starting an external logout redirect.
   */
  private clearLocalTokens(): void {
    this.accessTokenSubject.next(null);
    this.accessTokenExpiresAt = 0;
    this.refreshToken = null;
    this.activeAuthFlow = 'dealer';
    sessionStorage.removeItem('cognito_pkce_transaction');
  }

  private setTokens(tokens: CognitoTokenResponse): void {
    this.accessTokenSubject.next(tokens.access_token);
    this.accessTokenExpiresAt = Date.now() + Math.max(tokens.expires_in - 60, 0) * 1000;
    console.log('[REM flow] setTokens: access token stored in memory', {
      expiresIn: tokens.expires_in
    });
    if (tokens.refresh_token) {
      this.refreshToken = tokens.refresh_token;
    }
  }

  private assertConfiguration(configuration: CognitoConfiguration): void {
    if (!configuration.domain || !configuration.clientId || !configuration.redirectUri || !configuration.logoutUri) {
      throw new Error('Cognito configuration is incomplete.');
    }
  }

  // The path/query only; the hash (which may carry the first app's token) is never persisted or navigated to.
  private toSafeReturnUrl(returnUrl: string): string {
    const url = new URL(returnUrl, window.location.origin);
    const pathAndQuery = `${url.pathname}${url.search}`;
    return pathAndQuery.startsWith('/') && !pathAndQuery.startsWith('//') ? pathAndQuery : '/';
  }

  // The first app sends username/lang in the URL hash, not the query string, so both are parsed here.
  private getIncomingAppContext(returnUrl: string): IncomingAppContext {
    console.log('[REM flow] getIncomingAppContext: returnUrl', { returnUrl });
    const url = new URL(returnUrl, window.location.origin);
    const hashParams = this.parseHashParams(url);
    const languageValue = (hashParams.get('lang') ?? url.searchParams.get('lang'))?.toLowerCase();
    const username = (hashParams.get('username') ?? url.searchParams.get('username'))?.trim() || null;
    const usernameParts = username?.split('_') ?? [];
    const dealerCodeCandidate = usernameParts[0];

    console.log('[REM flow] getIncomingAppContext: usernameParts', { usernameParts });

    const context: IncomingAppContext = {
      language: languageValue === 'en' || languageValue === 'fr' ? languageValue : null,
      username,
      dealerCode: dealerCodeCandidate && /^\d{5}$/.test(dealerCodeCandidate) ? dealerCodeCandidate : null
    };

    console.log('[REM flow] getIncomingAppContext', {
      language: context.language,
      dealerCode: context.dealerCode
    });

    return context;
  }

  private parseHashParams(url: URL): URLSearchParams {
    return new URLSearchParams(url.hash.replace(/^#/, ''));
  }

  private logIncomingAppLink(): void {
    if (!environment.debugLogging) {
      return;
    }

    const url = new URL(window.location.href);
    const hashParams = this.parseHashParams(url);
    const hasIncomingParams = url.searchParams.has('username') || url.searchParams.has('lang')
      || hashParams.has('username') || hashParams.has('lang');
    if (!hasIncomingParams) {
      return;
    }

    console.info('[REM] Link received from first app:', window.location.href);
  }

  private createRandomValue(byteLength: number): string {
    const bytes = new Uint8Array(byteLength);
    window.crypto.getRandomValues(bytes);
    return this.toBase64Url(bytes);
  }

  private async createCodeChallenge(codeVerifier: string): Promise<string> {
    const data = new TextEncoder().encode(codeVerifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return this.toBase64Url(new Uint8Array(digest));
  }

  private toBase64Url(bytes: Uint8Array): string {
    let binary = '';
    bytes.forEach((byte) => binary += String.fromCharCode(byte));
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
}